import { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Button, OverlayTrigger, Tooltip, DropdownButton, Dropdown, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import AuthContext from "../../store/auth-context";
import { USERS_API_BASE_URL } from './../../constants';
import style from './OrgSetting.module.css';
import tableStyle from '../Style/TableModel.module.css';
import fontSize from '../Style/FontSize.module.css';
import common from '../Style/common.module.css';
import button from '../Style/ButtonStyle.module.css';
import font from '../Style/FontFamily.module.css';

import dateFormat from 'dateformat';
import ModifyDetails from '../ModifySchoolDetails/ModifyDetails';
import Pagination from '../UI/Pagination/Pagination';
import AddUser from './AddUser';
import AddOrganisation from './AddOrganistaion';
import ImportUsers from './ImportUsers';
import { deleteUser as deleteUserApi, fetchUserDetail, switchUser as switchUserApi } from '../../services/user.service';
import { getModuleList, getOrganisation } from '../../services/notification.services';
import { parseJwtToken } from '../../utils/jwt.utils';
import { STORAGE_KEYS, TOAST_MESSAGES, USER_TYPES } from './constants';
import { MdPadding } from 'react-icons/md';
import { borderRadius } from '@/styles';
import { has } from 'immutable';
import { UserData } from '@/types/user.types';

let pageSize = 10;

const OrganisationDetail = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const { orgUuid } = useParams();
    
    const [organisation, setOrganisation] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [key, setKey] = useState('details');
    
    // Users tab state
    const [users, setUsers] = useState<any[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [PageSize, setPageSize] = useState(pageSize);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<any>(null);
    const [userToImpersonate, setUserToImpersonate] = useState<any>(null);
    const [userDetail, setUserDetail] = useState<any>(null);
    const [userDetailPermission, setUserDetailPermission] = useState<any[]>([]);
    const [isImportUsersModalOpen, setIsImportUsersModalOpen] = useState(false);
    const [moduleList, setModuleList] = useState<any[]>([]);
    const [isOrgEditModalOpen, setIsOrgEditModalOpen] = useState(false);
    const [editMessage, setEditMessage] = useState('');
    
    // Setup progress state
    const [hasFeaturesEnabled, setHasFeaturesEnabled] = useState(true);
    //const [hasVerificationCodeGenerated, setHasVerificationCodeGenerated] = useState(true);
    const [hasAdminCreated, setHasAdminCreated] = useState(false);
    const [hasImpersonationChecked, setHasImpersonationChecked] = useState(false);
    const [isOrgConfigured, setIsOrgConfigured] = useState(true);
    
    // Features state
    const [features, setFeatures] = useState<any>({});
    const [isSavingFeatures, setIsSavingFeatures] = useState(false);

    // Integrations state
    const [integrations, setIntegrations] = useState<any>({
        org_sendgrid_username: '',
        org_sendgrid_password: '',
        org_wistia_project_id: '',
        org_sendgrid_hashid: '',
        sendgrid_api_key: '',
        epubOrgId: '',
        email_from_name: '',
        email_reply_to_email: ''
    });
    const [isSavingIntegrations, setIsSavingIntegrations] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isCreatingSendGrid, setIsCreatingSendGrid] = useState(false);
    const [isCreatingWistia, setIsCreatingWistia] = useState(false);
    
    // Test email state
    const [testEmailRecipient, setTestEmailRecipient] = useState('');
    const [testEmailSubject, setTestEmailSubject] = useState('Test Email from SchoolLink');
    const [testEmailBody, setTestEmailBody] = useState('This is a test email to verify SendGrid integration.');
    const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);

    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    const start = firstPageIndex;
    const end = lastPageIndex;
    const pageNumber = currentPage;

    useEffect(() => {
        if (!orgUuid) return;
        
        fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            }
        })
        .then(res => res.json())
        .then(data => {
            if (data.status === 404 || data.error) {
                setError(data.error || 'Failed to find organisation.');
            } else {
                setOrganisation(data);
                setFeatures({
                    CHANNEL_PUSH_NOTIFICATION: data.CHANNEL_PUSH_NOTIFICATION || 'OFF',
                    EDITOR_TEXT: data.EDITOR_TEXT || 'OFF',
                    EDIT_EPUB: data.EDIT_EPUB || 'OFF',
                    EDITOR_LINK: data.EDITOR_LINK || 'OFF',
                    EDIT_EMOJI: data.EDIT_EMOJI || 'OFF',
                    EDITOR_ATTACHMENT: data.EDITOR_ATTACHMENT || 'OFF',
                    EDITOR_EXTERNAL_LINK: data.EDITOR_EXTERNAL_LINK || 'OFF'
                });
                setIntegrations({
                    org_sendgrid_username: data.org_sendgrid_username || '',
                    org_sendgrid_password: data.org_sendgrid_password || '',
                    org_wistia_project_id: data.org_wistia_project_id || '',
                    org_sendgrid_hashid: data.org_sendgrid_hashid || '',
                    sendgrid_api_key: data.sendgrid_api_key || '',
                    epubOrgId: data.epubOrgId ?? '',
                    email_from_name: data.email_from_name || '',
                    email_reply_to_email: data.email_reply_to_email || ''
                });
                
                // Check if organization is configured (has SendGrid, Wistia, and email settings)
                const hasSendGrid = !!data.sendgrid_api_key;
                const hasWistia = !!data.org_wistia_project_id;
                const hasEmailFromName = !!data.email_from_name;
                const hasReplyToEmail = !!data.email_reply_to_email;
                setIsOrgConfigured(hasSendGrid && hasWistia && hasEmailFromName && hasReplyToEmail);
                
                // Check for features enabled - check data object directly, not features state
                const featuresEnabled = 
                    data.CHANNEL_PUSH_NOTIFICATION === 'ON' ||
                    data.EDITOR_TEXT === 'ON' ||
                    data.EDIT_EPUB === 'ON' ||
                    data.EDITOR_LINK === 'ON' ||
                    data.EDIT_EMOJI === 'ON' ||
                    data.EDITOR_ATTACHMENT === 'ON' ||
                    data.EDITOR_EXTERNAL_LINK === 'ON';
                setHasFeaturesEnabled(featuresEnabled);
                
                // Check for admin users
                fetch(`${USERS_API_BASE_URL}/admin/${orgUuid}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": authCtx.token
                    }
                })
                .then(res => res.json())
                .then(adminData => {
                    const hasAdmins = adminData && Array.isArray(adminData.userList) && adminData.userList.length > 0;
                    setHasAdminCreated(hasAdmins);
                // codeData && Array.isArray(codeData.verificationCode) && codeData.verificationCode.length > 0;
                // setHasVerificationCodeGenerated(hasCodes);
                })
                .catch(err => console.error('Error checking verification codes:', err));
            }
            setIsLoading(false);
        })
        .catch(err => {
            console.error(err);
            setError('Failed to load organisation details');
            setIsLoading(false);
        });
    }, [orgUuid, authCtx.token]);

    const loadUsers = useCallback(() => {
        setIsLoadingUsers(true);
        fetch(`${USERS_API_BASE_URL}/admin/${orgUuid}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            }
        })
        .then(res => res.json())
        .then(data => {
            if (data.status === 200 && data.userList) {
                setUsers(data.userList);
                // Check if first user has lastLogin
                if (data.userList.length > 0 && data.userList[0].lastLogin) {
                    setHasImpersonationChecked(true);
                } else {
                    setHasImpersonationChecked(false);
                }
            } else if (data && Array.isArray(data)) {
                setUsers(data);
                // Check if first user has lastLogin
                if (data.length > 0 && data[0].lastLogin) {
                    setHasImpersonationChecked(true);
                } else {
                    setHasImpersonationChecked(false);
                }
            }
            setIsLoadingUsers(false);
        })
        .catch(err => {
            console.error(err);
            setIsLoadingUsers(false);
        });
    }, [orgUuid, authCtx.token]);

    // Load users on initial mount to check impersonation status
    useEffect(() => {
        if (orgUuid) {
            loadUsers();
        }
    }, [orgUuid, loadUsers]);

    useEffect(() => {
        if (key === 'users' && orgUuid) {
            loadUsers();
        }
    }, [key, orgUuid, loadUsers]);

    // Load modules for user permissions
    useEffect(() => {
        if (!authCtx.token || !authCtx.isLoggedIn) return;
        
        getModuleList(authCtx.token)
            .then((resp) => setModuleList(Array.isArray(resp) ? resp : (resp as any)?.moduleList || []))
            .catch((err) => console.error('Error loading modules:', err));
    }, [authCtx.token, authCtx.isLoggedIn]);

    const handleFeatureToggle = (featureName: string, checked: boolean) => {
        setFeatures((prev: any) => ({
            ...prev,
            [featureName]: checked ? 'ON' : 'OFF'
        }));
    };

    const saveFeatures = () => {
        if (!organisation?.subscriptionPlan) {
            alert('Cannot update features: No subscription plan found');
            return;
        }
        
        setIsSavingFeatures(true);
        fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}/update-features`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            },
            body: JSON.stringify({
                planId: organisation.subscriptionPlan,
                orgId: orgUuid,
                ...features
            })
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) {
                alert(data.error);
            } else {
                alert('Features updated successfully');
            }
            setIsSavingFeatures(false);
        })
        .catch(err => {
            console.error(err);
            alert('Failed to update features');
            setIsSavingFeatures(false);
        });
    };

    const showHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(+e.target.value);
        setCurrentPage(1);
    };

    const paginateHandler = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const toggleUserModal = () => {
        setIsUserModalOpen((prev) => !prev);
        if (isUserModalOpen) {
            setUserDetail(null);
            setUserDetailPermission([]);
        }
    };

    const handleEditUser = async (uuid: string) => {
        if (!authCtx.token || !authCtx.isLoggedIn) return;
        
        try {
            setIsLoadingUsers(true);
            const data = await fetchUserDetail(authCtx.token, uuid);
            setUserDetail(data.user);
            setUserDetailPermission(data.userPermission);
            toggleUserModal();
        } catch (error) {
            console.error('Error fetching user detail:', error);
            toast.error('Failed to load user details');
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            const response = await deleteUserApi(authCtx.token, userToDelete.id);
            
            const message = response.status === 302 ? response.error : response.msg;
            toast.success(message || 'User deleted successfully');
            
            setUsers((prev) => prev.filter((user) => user.id !== userToDelete.id));
            setUserToDelete(null);
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        }
    };

    const confirmSwitchUser = async () => {
        if (!userToImpersonate) return;
        
        const uuid = userToImpersonate.uuid;
        console.log('confirmSwitchUser called with uuid:', uuid);
        
        // Validate required parameters
        if (!authCtx.token || authCtx.token.trim() === '') {
            toast.error('Authentication token is missing. Please log in again.');
            setUserToImpersonate(null);
            return;
        }

        if (!uuid || uuid.trim() === '') {
            console.error('UUID validation failed. UUID received:', uuid);
            toast.error('User ID is missing. Cannot switch user.');
            setUserToImpersonate(null);
            return;
        }

        try {
            // Save super admin credentials
            if (!localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_ID)) {
                localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_ID, authCtx.userData.user_id.toString());
                localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_TOKEN, authCtx.token);
                localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_NAME, authCtx.userData.name);
                localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_USERTYPE, authCtx.userData.usertype);
            }

            setIsLoadingUsers(true);
            const data = await switchUserApi(authCtx.token, uuid);
            const udata = data.response;

            if (!udata.token) {
                throw new Error('No token received');
            }

            const tokenUserData = parseJwtToken(udata.token);
            const orgId = tokenUserData.user.org;

            // Fetch organization details
            const orgDetail = await getOrganisation(orgId, udata.token);

            if (orgDetail.status === 400) {
                throw new Error('Could not get organisation this user belongs to');
            }

            const org = orgDetail;
            const superAdmin = localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_ID);
            const superAdminName = localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_NAME);
            const superAdminToken = localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_TOKEN);

            const userData = {
                uuid: udata.userId,
                superAdminId: superAdmin || undefined,
                superAdminName: superAdminName || undefined,
                superAdminToken: superAdminToken || undefined,
                user_id: udata.user_id,
                name: `${udata.firstName} ${udata.lastName}`,
                uid: tokenUserData.user.id,
                usertype: 'INTERNALADMIN' as UserData['usertype'], // Switched users are always INTERNALADMIN
                orgName: org.name,
                orgLogoUrl: org.logoUrl,
                token: udata.token,
                refreshToken: udata.refreshToken,
                orgId,
                epubOrgId: org.epubOrgId,
                orgWisitiaID: org.org_wistia_project_id,
                thumbImage: udata.thumbImage,
                CHANNEL_PUSH_NOTIFICATION: org.CHANNEL_PUSH_NOTIFICATION,
                EDITOR_ATTACHMENT: org.EDITOR_ATTACHMENT,
                EDITOR_EXTERNAL_LINK: org.EDITOR_EXTERNAL_LINK,
                EDITOR_LINK: org.EDITOR_LINK,
                EDITOR_TEXT: org.EDITOR_TEXT,
                EDIT_EMOJI: org.EDIT_EMOJI,
                EDIT_EPUB: org.EDIT_EPUB,
            };

            authCtx.login(userData);
            setUserToImpersonate(null);
            navigate('/notifications');
        } catch (error) {
            console.error('Error switching user:', error);
            toast.error(TOAST_MESSAGES.ERROR_SWITCHING_USER);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleSwitchUser = (user: any) => {
        setUserToImpersonate(user);
    };

    const handleIntegrationChange = (field: string, value: string) => {
        setIntegrations((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const sendTestEmail = () => {
        // Validate email settings first
        if (!integrations.email_from_name || !integrations.email_reply_to_email) {
            toast.error('Please configure From Name and Reply-To Email in Email Notification Settings before sending test emails');
            return;
        }
        
        if (!testEmailRecipient || !testEmailRecipient.trim()) {
            toast.error('Please enter a recipient email address');
            return;
        }
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(testEmailRecipient)) {
            toast.error('Please enter a valid email address');
            return;
        }
        
        if (!testEmailSubject || !testEmailSubject.trim()) {
            toast.error('Please enter an email subject');
            return;
        }
        
        if (!testEmailBody || !testEmailBody.trim()) {
            toast.error('Please enter email content');
            return;
        }
        
        setIsSendingTestEmail(true);
        fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}/send-test-email`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            },
            body: JSON.stringify({
                recipient: testEmailRecipient,
                subject: testEmailSubject,
                body: testEmailBody
            })
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) {
                toast.error(data.error);
            } else {
                toast.success('Test email sent successfully!');
                // Clear the form
                setTestEmailRecipient('');
                setTestEmailSubject('Test Email from SchoolLink');
                setTestEmailBody('This is a test email to verify SendGrid integration.');
            }
            setIsSendingTestEmail(false);
        })
        .catch(err => {
            console.error(err);
            toast.error('Failed to send test email');
            setIsSendingTestEmail(false);
        });
    };

    const handleEditOrganisation = () => {
        setIsOrgEditModalOpen(true);
    };

    const createSendGridIntegration = () => {
        setIsCreatingSendGrid(true);
        fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}/create_email_integration`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            }
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) {
                toast.error(data.error);
            } else {
                if (data.data?.existing) {
                    toast.info(data.message);
                } else {
                    toast.success(data.message);
                }
                // Refresh organisation data
                fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": authCtx.token
                    }
                })
                .then(res => res.json())
                .then(orgData => {
                    if (orgData && !orgData.error) {
                        setOrganisation(orgData);
                        setIntegrations({
                            org_sendgrid_username: orgData.org_sendgrid_username || '',
                            org_sendgrid_password: orgData.org_sendgrid_password || '',
                            org_wistia_project_id: orgData.org_wistia_project_id || '',
                            org_sendgrid_hashid: orgData.org_sendgrid_hashid || '',
                            sendgrid_api_key: orgData.sendgrid_api_key || '',
                            epubOrgId: orgData.epubOrgId || '',
                            email_from_name: orgData.email_from_name || '',
                            email_reply_to_email: orgData.email_reply_to_email || ''
                        });
                    }
                });
            }
            setIsCreatingSendGrid(false);
        })
        .catch(err => {
            console.error(err);
            toast.error('Failed to create SendGrid integration');
            setIsCreatingSendGrid(false);
        });
    };

    const createWistiaIntegration = () => {
        setIsCreatingWistia(true);
        fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}/create_wistia_integration`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            }
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) {
                toast.error(data.error);
            } else {
                if (data.data?.existing) {
                    toast.info(data.message);
                } else {
                    toast.success(data.message);
                }
                // Refresh organisation data
                fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": authCtx.token
                    }
                })
                .then(res => res.json())
                .then(orgData => {
                    if (orgData && !orgData.error) {
                        setOrganisation(orgData);
                        setIntegrations({
                            org_sendgrid_username: orgData.org_sendgrid_username || '',
                            org_sendgrid_password: orgData.org_sendgrid_password || '',
                            org_wistia_project_id: orgData.org_wistia_project_id || '',
                            org_sendgrid_hashid: orgData.org_sendgrid_hashid || '',
                            sendgrid_api_key: orgData.sendgrid_api_key || '',
                            epubOrgId: orgData.epubOrgId || '',
                            email_from_name: orgData.email_from_name || '',
                            email_reply_to_email: orgData.email_reply_to_email || ''
                        });
                    }
                });
            }
            setIsCreatingWistia(false);
        })
        .catch(err => {
            console.error(err);
            toast.error('Failed to create Wistia integration');
            setIsCreatingWistia(false);
        });
    };

    const saveIntegrations = () => {
        setIsSavingIntegrations(true);
        fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}/update-integrations`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            },
            body: JSON.stringify(integrations)
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) {
                toast.error(data.error);
            } else {
                toast.success('Integrations updated successfully');
                
                // Re-validate organization configuration status
                if (data.data) {
                    const hasSendGrid = !!data.data.sendgrid_api_key;
                    const hasWistia = !!data.data.org_wistia_project_id;
                    const hasEmailFromName = !!data.data.email_from_name;
                    const hasReplyToEmail = !!data.data.email_reply_to_email;
                    setIsOrgConfigured(hasSendGrid && hasWistia && hasEmailFromName && hasReplyToEmail);
                }
            }
            setIsSavingIntegrations(false);
        })
        .catch(err => {
            console.error(err);
            toast.error('Failed to update integrations');
            setIsSavingIntegrations(false);
        });
    };

    const handleStatusChange = (newStatus: string) => {
        if (!organisation) return;
        
        setIsUpdatingStatus(true);
        fetch(`${USERS_API_BASE_URL}/organisation/${orgUuid}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "Authorization": authCtx.token
            },
            body: JSON.stringify({ status: newStatus })
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) {
                toast.error(data.error);
            } else {
                setOrganisation({ ...organisation, status: newStatus });
                toast.success('Organisation status updated successfully');
            }
            setIsUpdatingStatus(false);
        })
        .catch(err => {
            console.error(err);
            toast.error('Failed to update organisation status');
            setIsUpdatingStatus(false);
        });
    };

    if (isLoading) {
        return (
            <div className={style.rightbar}>
                <div className={style['page-title']}>
                    <h4 className={`${fontSize.lgSize} ${common.pageTitle} ${font.fontMedium}`}>
                        Loading...
                    </h4>
                </div>
            </div>
        );
    }

    if (error || !organisation) {
        return (
            <div className={style.rightbar}>
                <div className={style['page-title']}>
                    <h4 className={`${fontSize.lgSize} ${common.pageTitle} ${font.fontMedium}`}>
                        Organisation Details
                    </h4>
                    <Button 
                        variant="danger"
                        onClick={() => navigate('/Organisation#organisations')}
                    >
                        <i className="icon icon-arrow-left"></i> Back to Organisations
                    </Button>
                </div>
                <div className={style.creatUserArea}>
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // Filter users for search
    const searchTextArray = searchText.toLowerCase().trim().split(' ');
    const filteredUsers = users.filter(user => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        return searchTextArray.every(term => 
            fullName.includes(term) || email.includes(term)
        );
    });

    const completedStepColor = '#45C096';
    const pendingStepColor = '#d0d0d0';

    return (
        <>
            {isOrgEditModalOpen && (
                <AddOrganisation
                    modalStatus={isOrgEditModalOpen}
                    onAction={() => {
                        setIsOrgEditModalOpen(false);
                        setEditMessage('');
                        // Reload organisation data after edit
                        window.location.reload();
                    }}
                    organisationDetail={organisation}
                    organisationList={null}
                    setOrganisationList={() => {}}
                    setOrganisationDetail={() => {}}
                    setMessage={setEditMessage}
                />
            )}

            {isUserModalOpen && (
                <AddUser
                    modalStatus={isUserModalOpen}
                    onAction={toggleUserModal}
                    setUserList={setUsers}
                    userList={users}
                    setMessage={() => {}}
                    userDetail={userDetail}
                    userDetailPermission={userDetailPermission}
                    moduleList={moduleList}
                    orgUuid={orgUuid}
                />
            )}

            {isImportUsersModalOpen && organisation && (
                <ImportUsers
                    modalStatus={isImportUsersModalOpen}
                    onAction={() => setIsImportUsersModalOpen(false)}
                    organisationDetail={organisation}
                    onImportComplete={() => {
                        // Reload users after import
                        loadUsers();
                    }}
                />
            )}

            <div className={style.rightbar}>
                <div className={style['page-title']} style={{display:'flex', flexDirection:'row', justifyContent:'space-between', alignItems:'center'}}>
                    <h4 className={`${fontSize.lgSize} ${common.pageTitle} ${font.fontMedium}`}>
                        <span style={{cursor: 'pointer'}} onClick={() => navigate('/Organisation#organisations')}>Organisations</span><span style={{color:"#3e3e3e"}}> &gt; {organisation.name}</span>
                    </h4>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        
                        <Button 
                            variant="light"
                            onClick={() => navigate('/Organisation#organisations')}
                        >
                            <i className="icon icon-arrow-left"></i> Back to Organisations
                        </Button>
                    </div>
                </div>
                
                {!(isOrgConfigured && hasFeaturesEnabled && hasAdminCreated && hasImpersonationChecked) && (
                    <div style={{
                        padding: '30px 40px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #e0e0e0',
                        margin: '20px 0'
                    }}>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            marginBottom: '25px',
                            color: '#333'
                        }}>
                            Organisation Setup Progress
                        </h3>
                        
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            maxWidth: '400px',
                            gap: '20px'
                        }}>
                            {/* Step 1 - Organisation Created */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    backgroundColor: completedStepColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#666' }}>Organisation Created</span>
                            </div>

                            {/* Step 2 - Configured */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    backgroundColor: isOrgConfigured ? completedStepColor : pendingStepColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#666' }}>Organisation Configurations & Integrations</span>
                            </div>

                            {/* Step 3 - Features Enabled */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    backgroundColor: hasFeaturesEnabled ? completedStepColor : pendingStepColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#666' }}>Features Enabled</span>
                            </div>

                            {/* Step 4 - at least one admin created for this org */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    backgroundColor: hasAdminCreated ? completedStepColor : pendingStepColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#666' }}>School Admin User generated</span>
                            </div>
                            {/* Step 5 - Impersonation checked */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    backgroundColor: hasImpersonationChecked ? completedStepColor : pendingStepColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#666' }}>Impersonation Tested</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className={style.creatUserArea} style={{flexDirection: 'column'}}>
                <Tabs
                    activeKey={key}
                    onSelect={(k) => setKey(k || 'details')}
                    className="mb-3"
                >
                    {/* Details Tab */}
                    <Tab eventKey="details" title="Details">
                        <div className="card" style={{border:'none'}}>
                            <div style={customStyles.content}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                                    <h5 className="card-title" style={{margin: 0}}>{organisation.name}</h5>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            
                            <DropdownButton
                                id="status-dropdown"
                                title={organisation?.status || 'ACTIVE'}
                                variant="primary"
                                disabled={isUpdatingStatus}
                                onSelect={(eventKey) => eventKey && handleStatusChange(eventKey)}
                                style={{
                                    fontSize: '14px'
                                }}
                            >
                                <Dropdown.Item eventKey="ACTIVE">Active</Dropdown.Item>
                                <Dropdown.Item eventKey="INACTIVE">Inactive</Dropdown.Item>
                                <Dropdown.Item eventKey="BLOCKED">Blocked</Dropdown.Item>
                                <Dropdown.Item eventKey="TRASHED">Trashed</Dropdown.Item>
                            </DropdownButton>
                        </div>

                                </div>
                                
                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Organisation ID</label>
                                            <p>{organisation.uuid}</p>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Customer ID</label>
                                            <p>{organisation.custID}</p>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold">ePublisher intrnal organisation id</label>
                                            <p>{organisation.epubOrgId}</p>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Email</label>
                                            <p>{organisation.email || 'N/A'}</p>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Phone</label>
                                            <p>{organisation.phone || 'N/A'}</p>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Website</label>
                                            <p>{organisation.website ? <a href={organisation.website} target="_blank" rel="noopener noreferrer">{organisation.website}</a> : 'N/A'}</p>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Subscription Plan</label>
                                            <p>{organisation.planName || 'N/A'}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Address</label>
                                            <p>
                                                {organisation.streetAddress && <>{organisation.streetAddress}<br /></>}
                                                {organisation.suburb && <>{organisation.suburb}<br /></>}
                                                {organisation.state && <>{organisation.state} </>}
                                                {organisation.postCode && <>{organisation.postCode}<br /></>}
                                                {organisation.country && <>{organisation.country}</>}
                                                {!organisation.streetAddress && !organisation.suburb && !organisation.state && 'N/A'}
                                            </p>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <p>
                                                <span className={organisation.status === 'ACTIVE' ? 'text-success' : 'text-danger'}>
                                                    {organisation.status}
                                                </span>
                                            </p>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Created Date</label>
                                            <p>{dateFormat(organisation.createDate, "dd-mm-yyyy HH:mm:ss")}</p>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Modified Date</label>
                                            <p>{dateFormat(organisation.modifiedDate, "dd-mm-yyyy HH:mm:ss")}</p>
                                        </div>

                                        {organisation.logoUrl && (
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Logo</label>
                                                <br />
                                                <img src={organisation.logoUrl} alt={organisation.name} style={{ maxWidth: '200px' }} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <Button 
                                            onClick={handleEditOrganisation}
                                            className={`${button.iconBtn} ${button.modalBtn} ${button.redButton}`}
                                        >
                                            <i className="icon icon-pencil"></i> Edit Details
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>

                    {/* Features Tab */}
                    <Tab eventKey="features" title="Features">
                        <div className="card" style={{border:'none'}}>
                            <div style={customStyles.content}>
                                <h5 className="card-title">Organisation Features</h5>
                                
                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <div className="form-check form-switch mb-3">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="pushNotifications"
                                                checked={features.CHANNEL_PUSH_NOTIFICATION === 'ON'}
                                                onChange={(e) => handleFeatureToggle('CHANNEL_PUSH_NOTIFICATION', e.target.checked)}
                                            />
                                            <label style={customStyles.labelLeftMargin} className="form-check-label" htmlFor="pushNotifications">
                                                Push Notifications
                                            </label>
                                        </div>

                                        <div className="form-check form-switch mb-3">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="textEditor"
                                                checked={features.EDITOR_TEXT === 'ON'}
                                                onChange={(e) => handleFeatureToggle('EDITOR_TEXT', e.target.checked)}
                                            />
                                            <label style={customStyles.labelLeftMargin} className="form-check-label" htmlFor="textEditor">
                                                Text Editor
                                            </label>
                                        </div>

                                        <div className="form-check form-switch mb-3">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="epubEditor"
                                                checked={features.EDIT_EPUB === 'ON'}
                                                onChange={(e) => handleFeatureToggle('EDIT_EPUB', e.target.checked)}
                                            />
                                            <label style={customStyles.labelLeftMargin} className="form-check-label" htmlFor="epubEditor">
                                                ePublisher Content
                                            </label>
                                        </div>

                                        <div className="form-check form-switch mb-3">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="linkEditor"
                                                checked={features.EDITOR_LINK === 'ON'}
                                                onChange={(e) => handleFeatureToggle('EDITOR_LINK', e.target.checked)}
                                            />
                                            <label style={customStyles.labelLeftMargin} className="form-check-label" htmlFor="linkEditor">
                                                Link Editor
                                            </label>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-check form-switch mb-3">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="emoji"
                                                checked={features.EDIT_EMOJI === 'ON'}
                                                onChange={(e) => handleFeatureToggle('EDIT_EMOJI', e.target.checked)}
                                            />
                                            <label style={customStyles.labelLeftMargin}  className="form-check-label" htmlFor="emoji">
                                                Emoji
                                            </label>
                                        </div>

                                        <div className="form-check form-switch mb-3">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="attachments"
                                                checked={features.EDITOR_ATTACHMENT === 'ON'}
                                                onChange={(e) => handleFeatureToggle('EDITOR_ATTACHMENT', e.target.checked)}
                                            />
                                            <label style={customStyles.labelLeftMargin}  className="form-check-label" htmlFor="attachments">
                                                Attachments
                                            </label>
                                        </div>

                                        <div className="form-check form-switch mb-3">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="externalLink"
                                                checked={features.EDITOR_EXTERNAL_LINK === 'ON'}
                                                onChange={(e) => handleFeatureToggle('EDITOR_EXTERNAL_LINK', e.target.checked)}
                                            />
                                            <label style={customStyles.labelLeftMargin}  className="form-check-label" htmlFor="externalLink">
                                                External Link
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="col-12">
                                        <Button 
                                            variant="danger"
                                            onClick={saveFeatures}
                                            disabled={isSavingFeatures}
                                        >
                                            {isSavingFeatures ? 'Saving...' : 'Save Features'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>

                    {/* Users Tab */}
                    <Tab eventKey="users" title="Users">
                        <div className={style.creatUserArea}>
                            <div className={style.settingbutton}>
                                <Button
                                    variant="danger"
                                    onClick={toggleUserModal}
                                >
                                    <i className="icon icon-plus-circle"></i> Create New User
                                </Button>

                                {organisation?.epubOrgId && (
                                    <Button
                                        variant="primary"
                                        onClick={() => setIsImportUsersModalOpen(true)}
                                        style={{ marginLeft: '10px' }}
                                    >
                                        <i className="icon icon-download"></i> Import Users from ePublisher
                                    </Button>
                                )}
                            </div>
                            <div className={style.tabs}>
                                <div className={`form-group ${tableStyle.hassearch}`}>
                                    <label htmlFor="SearchInput" className="hidenText">
                                        Search
                                    </label>
                                    <input
                                        value={searchText}
                                        id="SearchInput"
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className={tableStyle.searchInput}
                                        type="search"
                                        placeholder="Search by email or name..."
                                    />
                                    <i className={`icon icon-search2 ${tableStyle.searchIcon}`}></i>
                                </div>
                            </div>
                        </div>

                        {isLoadingUsers ? (
                            <div className="text-center py-4" style={{zIndex:0}}>
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="table-design">
                                    <table className={`table table-hover ${style.categorytable}`}>
                                        <thead className={style.heading}>
                                            <tr>
                                                <th scope="col">First Name</th>
                                                <th scope="col">Last Name</th>
                                                <th scope="col">Email Address</th>
                                                <th scope="col">User Type</th>
                                                <th scope="col">Status</th>
                                                <th scope="col">Phone</th>
                                                <th scope="col">Created Date</th>
                                                <th scope="col">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className={style.tablebody}>
                                            {filteredUsers.length > 0 ? (
                                                filteredUsers.slice(start, end).map((user: any) => {
                                                    console.log('User object:', user, 'UUID:', user.uuid);
                                                    return (
                                                    <tr key={user.id}>
                                                        <td>{user.firstName}</td>
                                                        <td>{user.lastName}</td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                {!authCtx.userData.orgId ? (
                                                                    <OverlayTrigger
                                                                        placement="right"
                                                                        delay={{ show: 50, hide: 400 }}
                                                                        overlay={(props) => (
                                                                            <Tooltip id="button-tooltip" {...props}>
                                                                                Click to Impersonate
                                                                            </Tooltip>
                                                                        )}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            className={style.selectUser}
                                                                            onClick={() => {
                                                                                console.log('Clicking impersonate for user:', user);
                                                                                handleSwitchUser(user);
                                                                            }}
                                                                        >
                                                                            {user.email}
                                                                        </button>
                                                                    </OverlayTrigger>
                                                                ) : (
                                                                    <span>{user.email}</span>
                                                                )}
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={(props) => (
                                                                        <Tooltip id={`copy-tooltip-${user.id}`} {...props}>
                                                                            Copy Email
                                                                        </Tooltip>
                                                                    )}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        style={{
                                                                            border: 'none',
                                                                            background: 'transparent',
                                                                            cursor: 'pointer',
                                                                            padding: '4px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            color: '#666'
                                                                        }}
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(user.email);
                                                                            toast.success('Email copied to clipboard!');
                                                                        }}
                                                                    >
                                                                        <i className="icon icon-copy" style={{ fontSize: '14px' }}></i>
                                                                    </button>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </td>
                                                        <td>{user.usertype}</td>
                                                        <td>
                                                            <i className={`fa fa-check ${style.statusActive}`}></i>
                                                        </td>
                                                        <td>{user.phoneNumber || 'N/A'}</td>
                                                        <td>{dateFormat(user.createDate, "dd-mm-yyyy HH:mm:ss")}</td>
                                                        <td>
                                                            {(authCtx.userData.usertype === USER_TYPES.OWNER || authCtx.userData.usertype === 'INTERNALADMIN') && (
                                                                <span className="actionStyleButton impersonateBtn">
                                                                    <i className="icon icon-key" onClick={() => {
                                                                        console.log('Key icon clicked for user:', user);
                                                                        handleSwitchUser(user);
                                                                    }}></i>
                                                                </span>
                                                            )}
                                                            <span className="actionStyleButton">
                                                                <button type="button" title="Edit" onClick={() => handleEditUser(user.uuid)}>
                                                                    <i className="icon icon-pencil"></i>
                                                                </button>
                                                            </span>
                                                            <span className="actionStyleButton">
                                                                <button type="button" title="Delete" onClick={() => setUserToDelete(user)}>
                                                                    <i className="fa fa-trash-o"></i>
                                                                </button>
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={8} className={style.noRecordCell}>
                                                        <div className={style.loader}>
                                                            <div className={style['text-loader']}>
                                                                {searchText ? 'No users found matching your search' : 'No record found'}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>

                                    <div className={`row justify-content-between ${tableStyle.listShow}`}>
                                        {filteredUsers.length > 10 && (
                                            <div className="col-4">
                                                <label className={tableStyle.listLabel}>
                                                    Show
                                                    <select
                                                        className={`form-select ${tableStyle.listSelect}`}
                                                        value={PageSize}
                                                        onChange={showHandler}
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={20}>20</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                </label>
                                            </div>
                                        )}

                                        <div className="col-4">
                                            <div className={tableStyle.paginationContainer}>
                                                <Pagination
                                                    currentPage={pageNumber}
                                                    totalCount={filteredUsers.length}
                                                    pageSize={PageSize}
                                                    onPageChange={(page) => setCurrentPage(page)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </Tab>

                    {/* Integrations Tab */}
                    <Tab eventKey="integrations" title="Configurations & Integrations">
                        <div className="card" style={{border:'none'}}>
                            <div style={customStyles.content}>
                                
                                <Card className="mb-4">
                                    <Card.Header>
                                        <h5 className="mb-0">Email Notification Settings</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">From Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={integrations.email_from_name}
                                                        onChange={(e) => handleIntegrationChange('email_from_name', e.target.value)}
                                                        placeholder="Enter from name for emails"
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">Reply-To Email</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={integrations.email_reply_to_email}
                                                        onChange={(e) => handleIntegrationChange('email_reply_to_email', e.target.value)}
                                                        placeholder="Enter reply-to email address"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <Button 
                                                    variant="danger"
                                                    onClick={saveIntegrations}
                                                    disabled={isSavingIntegrations}
                                                >
                                                    {isSavingIntegrations ? 'Saving...' : 'Save Email Settings'}
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card className="mb-4">
                                    <Card.Header>
                                        <h5 className="mb-0">Email Integration (SendGrid)</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="alert alert-info" style={{borderRadius: '8px', padding: '15px'}}>
                                                    <strong>Quick Setup:</strong> Automatically creates a SendGrid subuser account with all required settings configured
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <Button 
                                                    variant="secondary"
                                                    onClick={createSendGridIntegration}
                                                    disabled={isCreatingSendGrid || !!integrations.org_sendgrid_username}
                                                    style={{width: '200px'}}
                                                >
                                                    {isCreatingSendGrid ? 'Creating...' : 
                                                     integrations.org_sendgrid_username ? 'Email Integration Active' : 'Create Email Integration'}
                                                </Button>
                                                {integrations.org_sendgrid_username && (
                                                    <small className="text-success d-block mt-2">
                                                        ✓ Username: {integrations.org_sendgrid_username}
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        <hr className="my-4" />

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">SendGrid Username</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={integrations.org_sendgrid_username}
                                                        onChange={(e) => handleIntegrationChange('org_sendgrid_username', e.target.value)}
                                                        placeholder="SendGrid username"
                                                        disabled
                                                        style={{backgroundColor: '#f8f9fa'}}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">SendGrid API Key</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={integrations.sendgrid_api_key}
                                                        onChange={(e) => handleIntegrationChange('sendgrid_api_key', e.target.value)}
                                                        placeholder="Enter SendGrid API key"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <Button 
                                                    variant="danger"
                                                    onClick={saveIntegrations}
                                                    disabled={isSavingIntegrations}
                                                >
                                                    {isSavingIntegrations ? 'Saving...' : 'Save SendGrid Settings'}
                                                </Button>
                                            </div>

                                        </div>
                                        
                                        <hr className="my-4" />
                                        
                                        {/* Test Email Section */}
                                        <div className="row">
                                            <div className="col-12">
                                                <h6 style={{fontWeight: '600', marginBottom: '15px'}}>Test Email Configuration</h6>
                                                <div className="alert alert-info" style={{borderRadius: '8px', padding: '15px', marginBottom: '20px'}}>
                                                    <strong>Note:</strong> Make sure "From Name" and "Reply-To Email" are configured above before sending test emails.
                                                </div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Recipient Email *</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={testEmailRecipient}
                                                        onChange={(e) => setTestEmailRecipient(e.target.value)}
                                                        placeholder="Enter recipient email address"
                                                        disabled={isSendingTestEmail}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Subject *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={testEmailSubject}
                                                        onChange={(e) => setTestEmailSubject(e.target.value)}
                                                        placeholder="Email subject"
                                                        disabled={isSendingTestEmail}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label className="form-label">Email Content *</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={4}
                                                        value={testEmailBody}
                                                        onChange={(e) => setTestEmailBody(e.target.value)}
                                                        placeholder="Enter test email content"
                                                        disabled={isSendingTestEmail}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-12">
                                                <Button
                                                    variant="success"
                                                    onClick={sendTestEmail}
                                                    disabled={isSendingTestEmail || !integrations.email_from_name || !integrations.email_reply_to_email}
                                                >
                                                    {isSendingTestEmail ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Sending...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="icon icon-mail" style={{marginRight: '5px'}}></i>
                                                            Send Test Email
                                                        </>
                                                    )}
                                                </Button>
                                                {(!integrations.email_from_name || !integrations.email_reply_to_email) && (
                                                    <small className="text-muted d-block mt-2">
                                                        Please configure From Name and Reply-To Email above to enable test emails
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        
                                    </Card.Body>
                                </Card>

                                <Card className="mb-4">
                                    <Card.Header>
                                        <h5 className="mb-0">Video Integration (Wistia)</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="alert alert-info" style={{borderRadius: '8px', padding: '15px'}}>
                                                    <strong>Quick Setup:</strong> Automatically creates a Wistia project for hosting organisation videos
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <Button 
                                                    variant="secondary"
                                                    onClick={createWistiaIntegration}
                                                    disabled={isCreatingWistia || !!integrations.org_wistia_project_id}
                                                    style={{width: '200px'}}
                                                >
                                                    {isCreatingWistia ? 'Creating...' : 
                                                     integrations.org_wistia_project_id ? 'Video Integration Active' : 'Create Video Integration'}
                                                </Button>
                                                {integrations.org_wistia_project_id && (
                                                    <small className="text-success d-block mt-2">
                                                        ✓ Project ID: {integrations.org_wistia_project_id}
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        <hr className="my-4" />

                                        <div className="row">
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">Wistia Project ID</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={integrations.org_wistia_project_id}
                                                        onChange={(e) => handleIntegrationChange('org_wistia_project_id', e.target.value)}
                                                        placeholder="Enter Wistia project ID"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <Button 
                                                    variant="danger"
                                                    onClick={saveIntegrations}
                                                    disabled={isSavingIntegrations}
                                                >
                                                    {isSavingIntegrations ? 'Saving...' : 'Save Wistia Settings'}
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card className="mb-4">
                                    <Card.Header>
                                        <h5 className="mb-0">Other Configuration</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">ePublisher Organisation internal Id</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={integrations.epubOrgId}
                                                        onChange={(e) => handleIntegrationChange('epubOrgId', e.target.value)}
                                                        placeholder="ePublisher Organisation internal Id"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <Button 
                                                    variant="danger"
                                                    onClick={saveIntegrations}
                                                    disabled={isSavingIntegrations}
                                                >
                                                    {isSavingIntegrations ? 'Saving...' : 'Save ePublisher Settings'}
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </Tab>
                        
                   
                </Tabs>
            </div>
        </div>

        <Modal
            isOpen={!!userToDelete}
            className="alertBgContainer"
            contentLabel="Delete User Confirmation"
        >
            <div className="alertBox">
                <h4 className={tableStyle.alertTitle}>Alert</h4>
                <p className={`${fontSize.smSize} ${font.fontRegular} ${common.pTag}`}>
                    Are you sure want to <b className={font.fontBold}>delete this user?</b>
                </p>
                <div className="bottomArea">
                    <Button
                        variant="secondary"
                        onClick={() => setUserToDelete(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleDeleteUser}
                        style={{marginLeft: '10px'}}
                    >
                        Yes
                    </Button>
                </div>
            </div>
        </Modal>

        <Modal
            isOpen={!!userToImpersonate}
            className="alertBgContainer"
            contentLabel="Impersonate User Confirmation"
        >
            <div className="alertBox">
                <h4 className={tableStyle.alertTitle}>Confirm Impersonation</h4>
                <p className={`${fontSize.smSize} ${font.fontRegular} ${common.pTag}`}>
                    Are you sure you want to <b className={font.fontBold}>impersonate {userToImpersonate?.firstName} {userToImpersonate?.lastName}</b>?
                    <br />
                    <small>({userToImpersonate?.email})</small>
                </p>
                <p className={`${fontSize.smSize} ${font.fontRegular} ${common.pTag}`} style={{marginTop: '10px', color: '#666'}}>
                    You will be logged in as this user and can perform actions on their behalf.
                </p>
                <div className="bottomArea">
                    <Button
                        variant="secondary"
                        onClick={() => setUserToImpersonate(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={confirmSwitchUser}
                        disabled={isLoadingUsers}
                        style={{marginLeft: '10px'}}
                    >
                        {isLoadingUsers ? 'Switching...' : 'Yes, Impersonate'}
                    </Button>
                </div>
            </div>
        </Modal>
    </>
    );
};

const customStyles = {
    content: {
        padding: '20px 5px'
      
    },
    labelLeftMargin: {
        marginLeft: '10px'
    }
};


export default OrganisationDetail;
